<template>
  <div class="app-current-bill" v-if="!loading" :class="[{ ['terminated']: contractTerminated }, customClassName]">
    <SimonModal
      :is-visible="showCreditModal"
      @hide="showCreditModal = false"
      id="credit-info-modal"
    >
      <template v-slot:modalTitle>
        <slot name="titleCreditModal"></slot>
      </template>
      <slot name="txtCreditModal"></slot>
    </SimonModal>
    <div class="card">
      <div class="card-head">
        <h3>
          <slot name="txtHeadline">Deine aktuelle Rechnung</slot>
        </h3>
        <div class="bill-period-info">
          <dyn-slot :slot-data="slotData">
            <slot name="txtBillPeriod">Zeitraum xx.xx.xxxx - xx.xx.xxxx</slot>
          </dyn-slot>
        </div>
      </div>

      <div class="fees-list">
        <div class="tariffFee row" v-if="showTariffFee">
          <div class="name">
            <slot name="txtTariffFee">Dein Basispreis</slot>
          </div>
          <div class="price">{{ price(basicFee) }}</div>
        </div>

        <div class="connectionFee row" v-if="showConnectionFee">
          <div class="name">
            <slot name="txtConnectionFee">Verbindungskosten</slot>
          </div>
          <div class="price">{{ price(!this.contractTerminated ? currentCustomer.balance : 0) }}</div>
        </div>

        <div class="discountFee" v-if="showDiscountFee && filteredCreditIncentives.length > 0">
          <PromoCurrentBillDiscount class="row"
                                    v-for="incentive in filteredCreditIncentives"
                                    :key="incentive.incentiveId"
                                    :description="incentive.description"
                                    :discount="incentive.discount"
                                    :expirationDate="incentive.expirationDate"
                                    context="REGISTER_MEMBER"
                                    :default=false
                                    @isVisible="setIsPromoCurrentBillDiscountVisible"
          ></PromoCurrentBillDiscount>
        </div>

        <div class="sumFee row" v-if="showSumFee">
          <div class="name">
            <slot v-if="isSumFee" name="txtSumFee">Gesamtkosten</slot>
            <slot v-else name="txtCredit">Dein Guthaben</slot>
            <a v-if="!isSumFee" class="info-link-icon" @click="showCreditModal=true;"></a>
          </div>
          <div class="price total-price" :class="dynamicSumFeeClass">{{ pricePositiv(billedSum) }}</div>
        </div>
      </div>

      <div v-if="showError" v-html="this.errorMsg" class="error"></div>
      <div class="cta" v-if="showCta">
        <slot name="ctaBills"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import { splitError } from '@/helper/splitError'
import price from '@/mixins/formatters/price'
import dateFormatter from '@/mixins/formatters/dateFormatter'
import syncProperty from '@/mixins/syncProperty'
import customerContractState from '@/mixins/customerContractState'
import beErrors from '@/mixins/beErrors'
import { mapGetters } from 'vuex'
import PromoCurrentBillDiscount from '@/components/generic/Promotions/CurrentBillDiscount.vue'
import SimonModal from '@/components/modal/SimonModal.vue'

export default {
  name: 'CurrentBill',
  components: { SimonModal, PromoCurrentBillDiscount },
  props: {
    customClassName: {
      type: String,
      default: ''
    },
    showTariffFee: {
      type: Boolean,
      default: true
    },
    showConnectionFee: {
      type: Boolean,
      default: true
    },
    showDiscountFee: {
      type: Boolean,
      default: true
    },
    showSumFee: {
      type: Boolean,
      default: true
    },
    showCta: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      loading: true,
      showError: false,
      showCreditModal: false,
      modalType: '',
      tariffs: {
        bookedTariff: null
      },
      currentCustomer: {
        balance: 0
      },
      tariffoptions: {
        bookedTariffoptions: null
      },
      errorMsg: '',
      discount: 0,
      isPromoCurrentBillDiscountVisible: false
    }
  },
  mixins: [
    price,
    dateFormatter,
    syncProperty('loaded', 'loaded_'),
    customerContractState(),
    beErrors
  ],
  methods: {
    setIsPromoCurrentBillDiscountVisible () {
      this.isPromoCurrentBillDiscountVisible = true
    }
  },
  computed: {
    ...mapGetters('promotion/activePromotions', [
      'creditIncentivesForCurrentTariff'
    ]),
    slotData () {
      return {
        billEndDate: this.dateFormatter(new Date(this.actDate.getFullYear(), this.actDate.getMonth() + 1, 0)), // last day of actual month!
        billStartDate: this.dateFormatter(new Date(this.actDate.getFullYear(), this.actDate.getMonth(), 1)) // first day of actual month!
      }
    },
    isContractActiveOrPaused () {
      return this.currentCustomerContract?.contract?.state === this.CONTRACT_STATE_ACTIVE || this.contractPauseActive
    },
    isSumFee () {
      return !(this.billedSum < 0 && this.isContractActiveOrPaused)
    },
    dynamicSumFeeClass () {
      return this.showError
        ? 'error-price'
        : this.contractTerminated
          ? 'terminated-price'
          : this.billedSum >= 0 && this.isContractActiveOrPaused
            ? 'positive-price'
            : 'negative-price'
    },
    basicFee () {
      let billedPrice = this.tariffs?.bookedTariff?.billedPrice || 0
      if (this.contractPauseActive || this.contractTerminated || !billedPrice) {
        return 0
      }
      if (this.tariffoptions?.bookedTariffoptions?.bookedTariffoptions) {
        this.tariffoptions?.bookedTariffoptions?.bookedTariffoptions.forEach((option) => {
          if (option.automaticExtension && ['CDL', 'CAN', 'TER'].includes(option.statusKey)) {
            billedPrice += option.billedPrice
          }
        })
      }
      return billedPrice
    },
    billedSum () {
      if (this.contractPauseActive && this.currentCustomer.balance !== null) {
        return parseFloat(this.currentCustomer.balance)
      } else if (this.contractTerminated) {
        return 0
      }
      return parseFloat(this.basicFee) + parseFloat(this.currentCustomer.balance) - parseFloat(this.addedCreditDiscount)
    },
    actDate () {
      return new Date()
    },
    filteredCreditIncentives () {
      const incentives = Array.isArray(this.creditIncentivesForCurrentTariff) ? this.creditIncentivesForCurrentTariff : []
      const filteredIncentives = incentives.filter(incentive => {
        return incentive.entity !== 'TARIFF' || incentive.feeType !== 'MONTHLY_FEE'
      })
      return filteredIncentives
    },
    addedCreditDiscount () {
      let sumDiscount = 0
      this.filteredCreditIncentives.forEach(incentive => {
        sumDiscount += incentive.discount
      })
      return sumDiscount
    }
  },
  apollo: {
    tariffs: function () {
      this.loading = true
      return wrapQuery(
        gql`query bookedTariff {
          tariffs{
            bookedTariff {
              billedPrice
              basicFee
            }
          }
          currentCustomer {
            balance
          }
        }`, this, ['tariffs', 'currentCustomer'], {}, () => {
          if (!this.$apollo.queries.tariffs.loading) {
            this.loading = false
            this.showError = false
            this.errorMsg = ''
            this.loaded_ = true
            this.$emit('loaded')
          }
        },
        {
          fetchPolicy: 'cache-first'
        },
        (error) => {
          this.loading = false
          this.currentCustomer.balance = 0
          this.errorMsg = this.beErrorGetLastMessage()
          if (this.errorMsg === '') {
            splitError(error).forEach(e => {
              this.errorMsg = e.message
            })
          }
          if (!this.showError) {
            const data = {
              type: 'technical',
              message: this.errorMsg
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError = true
          this.loaded_ = true
          this.$emit('loaded')
        }
      )
    },
    bookedTariffOptions: function () {
      return wrapQuery(
        gql`query bookedTariffOptions {
          tariffoptions{
            bookedTariffoptions {
              bookedTariffoptions {
                automaticExtension
                tariffoptionId
                name
                statusKey
                billedPrice
                price
              }
            }
          }
        }`, this, 'tariffoptions', {}, (data) => {
        },
        {
          fetchPolicy: 'no-cache'
        },
        (error) => {
          this.errorMsg = this.beErrorGetLastMessage()
          if (this.errorMsg === '') {
            splitError(error).forEach(e => {
              this.errorMsg = e.message
            })
          }
          if (!this.showError) {
            const data = {
              type: 'technical',
              message: this.errorMsg
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError = true
        }
      )
    }
  }
}
</script>
